import {
    Body,
    Constellation,
    Equator,
    Horizon,
    Illumination,
    SearchAltitude,
    SearchRiseSet
} from 'astronomy-engine'

import { getMoonState } from '@/lib/astro/moon'
import { COMPASS_WORDS, compassPoint, observer, raDecToVector } from '@/lib/astro/sky'
import { localNoon, zonedParts } from '@/lib/time'

export type PlanetKey = 'mercury' | 'venus' | 'mars' | 'jupiter' | 'saturn' | 'uranus' | 'neptune'

export type PlanetInfo = {
    key: PlanetKey
    body: Body
    name: string
    look: string
    nakedEye: boolean
}

export const PLANETS: PlanetInfo[] = [
    { key: 'mercury', body: Body.Mercury, name: 'Mercury', look: 'small, pinkish point low in the twilight', nakedEye: true },
    { key: 'venus', body: Body.Venus, name: 'Venus', look: 'brilliant white point, the brightest thing after the Moon', nakedEye: true },
    { key: 'mars', body: Body.Mars, name: 'Mars', look: 'steady, orange-red point', nakedEye: true },
    { key: 'jupiter', body: Body.Jupiter, name: 'Jupiter', look: 'steady, cream-white point that outshines every star near it', nakedEye: true },
    { key: 'saturn', body: Body.Saturn, name: 'Saturn', look: 'steady, pale gold point', nakedEye: true },
    { key: 'uranus', body: Body.Uranus, name: 'Uranus', look: 'faint blue-green dot, best in binoculars', nakedEye: false },
    { key: 'neptune', body: Body.Neptune, name: 'Neptune', look: 'tiny blue disc, a telescope target', nakedEye: false }
]

const RADIUS_KM: Record<string, number> = {
    sun: 695700,
    moon: 1737.4,
    mercury: 2439.7,
    venus: 6051.8,
    mars: 3389.5,
    jupiter: 69911,
    saturn: 58232,
    uranus: 25362,
    neptune: 24622
}

export const AU_KM = 149597870.7

export type SkyBody = {
    key: string
    name: string
    kind: 'sun' | 'moon' | 'planet'
    /** J2000 unit vector, for plotting against the star catalogue. */
    vector: [number, number, number]
    raHours: number
    decDeg: number
    alt: number
    az: number
    magnitude: number
    distanceAU: number
    constellation: string
    phaseFraction: number
    /** Apparent diameter in arcseconds. */
    diameter: number
}

const snapshot = (key: string, name: string, kind: SkyBody['kind'], body: Body, date: Date): SkyBody => {
    const j2000 = Equator(body, date, observer, false, true)
    const ofDate = Equator(body, date, observer, true, true)
    const horizon = Horizon(date, observer, ofDate.ra, ofDate.dec, 'normal')
    const illumination = Illumination(body, date)
    const distanceKm = j2000.dist * AU_KM

    return {
        key,
        name,
        kind,
        vector: raDecToVector(j2000.ra * 15, j2000.dec),
        raHours: j2000.ra,
        decDeg: j2000.dec,
        alt: horizon.altitude,
        az: horizon.azimuth,
        magnitude: illumination.mag,
        distanceAU: j2000.dist,
        constellation: Constellation(j2000.ra, j2000.dec).name,
        phaseFraction: illumination.phase_fraction,
        diameter: 2 * Math.atan(RADIUS_KM[key] / distanceKm) * (180 / Math.PI) * 3600
    }
}

/** Sun, Moon and planets as seen from the observatory at one instant. */
export const skySnapshot = (date: Date): SkyBody[] => [
    snapshot('sun', 'Sun', 'sun', Body.Sun, date),
    snapshot('moon', 'Moon', 'moon', Body.Moon, date),
    ...PLANETS.map(planet => snapshot(planet.key, planet.name, 'planet', planet.body, date))
]

export const altitudeOf = (body: Body, date: Date): { alt: number, az: number } => {
    const ofDate = Equator(body, date, observer, true, true)
    const horizon = Horizon(date, observer, ofDate.ra, ofDate.dec, 'normal')

    return { alt: horizon.altitude, az: horizon.azimuth }
}

export type NightEvents = {
    key: string
    sunset: Date | null
    civilDusk: Date | null
    nauticalDusk: Date | null
    astroDusk: Date | null
    astroDawn: Date | null
    nauticalDawn: Date | null
    civilDawn: Date | null
    sunrise: Date | null
    /** Start and end of the darkest part of the night. */
    darkStart: Date
    darkEnd: Date
    darkness: 'astronomical' | 'nautical'
    moonrise: Date | null
    moonset: Date | null
}

const search = (body: Body, direction: number, from: Date, altitude?: number): Date | null => {
    try {
        const result = altitude === undefined
            ? SearchRiseSet(body, observer, direction, from, 1)
            : SearchAltitude(body, observer, direction, from, 1, altitude)

        return result ? result.date : null
    }
    catch {
        return null
    }
}

/** Twilight, darkness, Sun and Moon events for the evening of a calendar day. */
export const nightEvents = (key: string): NightEvents => {
    const noon = localNoon(key)
    const sunset = search(Body.Sun, -1, noon)
    const civilDusk = search(Body.Sun, -1, noon, -6)
    const nauticalDusk = search(Body.Sun, -1, noon, -12)
    const astroDusk = search(Body.Sun, -1, noon, -18)
    const after = sunset ?? noon
    const astroDawn = astroDusk ? search(Body.Sun, 1, astroDusk, -18) : null
    const nauticalDawn = nauticalDusk ? search(Body.Sun, 1, nauticalDusk, -12) : null
    const civilDawn = search(Body.Sun, 1, after, -6)
    const sunrise = search(Body.Sun, 1, after)
    const fallbackStart = new Date(noon.getTime() + 10 * 3600 * 1000)
    const darkStart = astroDusk ?? nauticalDusk ?? fallbackStart
    const darkEnd = astroDawn ?? nauticalDawn ?? new Date(darkStart.getTime() + 4 * 3600 * 1000)

    return {
        key,
        sunset,
        civilDusk,
        nauticalDusk,
        astroDusk,
        astroDawn,
        nauticalDawn,
        civilDawn,
        sunrise,
        darkStart,
        darkEnd,
        darkness: astroDusk && astroDawn ? 'astronomical' : 'nautical',
        moonrise: search(Body.Moon, 1, noon),
        moonset: search(Body.Moon, -1, noon)
    }
}

export type PlanetNight = {
    key: PlanetKey
    name: string
    look: string
    nakedEye: boolean
    rise: Date | null
    set: Date | null
    visibleFrom: Date | null
    visibleTo: Date | null
    bestTime: Date | null
    bestAlt: number
    bestAz: number
    magnitude: number
    constellation: string
    vector: [number, number, number]
}

const STEP_MS = 15 * 60 * 1000
const MIN_ALTITUDE = 8

const nightSpan = (events: NightEvents): { start: Date, end: Date } => {
    const start = events.civilDusk ?? events.sunset ?? new Date(localNoon(events.key).getTime() + 8 * 3600 * 1000)
    const end = events.civilDawn ?? events.sunrise ?? new Date(start.getTime() + 11 * 3600 * 1000)

    return { start, end }
}

/** Which planets climb above the trees tonight, when, and where to look. */
export const planetsForNight = (events: NightEvents): PlanetNight[] => {
    const { start, end } = nightSpan(events)
    const noon = localNoon(events.key)

    return PLANETS.map(planet => {
        let visibleFrom: Date | null = null
        let visibleTo: Date | null = null
        let bestTime: Date | null = null
        let bestAlt = -90
        let bestAz = 0

        for (let t = start.getTime(); t <= end.getTime(); t += STEP_MS) {
            const time = new Date(t)
            const { alt, az } = altitudeOf(planet.body, time)

            if (alt > MIN_ALTITUDE) {
                if (!visibleFrom) visibleFrom = time
                visibleTo = time
            }

            if (alt > bestAlt) {
                bestAlt = alt
                bestAz = az
                bestTime = time
            }
        }

        const reference = bestTime ?? start
        const j2000 = Equator(planet.body, reference, observer, false, true)

        return {
            key: planet.key,
            name: planet.name,
            look: planet.look,
            nakedEye: planet.nakedEye,
            rise: search(planet.body, 1, noon),
            set: search(planet.body, -1, noon),
            visibleFrom,
            visibleTo,
            bestTime: visibleFrom ? bestTime : null,
            bestAlt,
            bestAz,
            magnitude: Illumination(planet.body, reference).mag,
            constellation: Constellation(j2000.ra, j2000.dec).name,
            vector: raDecToVector(j2000.ra * 15, j2000.dec)
        }
    })
}

export type ViewingWindow = {
    start: Date
    end: Date
    score: number
}

/** Cloud cover from 0 to 100 at a time, or null when there is no forecast. */
export type CloudLookup = (time: Date) => number | null

/** The longest stretch of the night that is dark, Moon-free and, when known, clear. */
export const bestViewingWindow = (events: NightEvents, cloudAt?: CloudLookup): ViewingWindow | null => {
    const { start, end } = nightSpan(events)
    const step = 10 * 60 * 1000
    const samples: { time: Date, score: number }[] = []

    for (let t = start.getTime(); t <= end.getTime(); t += step) {
        const time = new Date(t)
        const sun = altitudeOf(Body.Sun, time)
        const moon = altitudeOf(Body.Moon, time)
        const fraction = getMoonState(time).fraction
        const darkness = sun.alt <= -18 ? 1 : sun.alt <= -12 ? 0.78 : sun.alt <= -9 ? 0.45 : 0.15
        const moonGlare = moon.alt > 0 ? fraction * (0.35 + 0.65 * Math.sin((moon.alt * Math.PI) / 180)) : 0
        const cloud = cloudAt?.(time)
        const clear = cloud === null || cloud === undefined ? 1 : Math.max(0, 1 - cloud / 100)

        samples.push({ time, score: darkness * (1 - 0.7 * moonGlare) * clear })
    }

    const top = Math.max(...samples.map(sample => sample.score))

    if (!Number.isFinite(top) || top < 0.2) return null

    const threshold = top * 0.8
    let best: ViewingWindow | null = null
    let runStart = -1

    samples.forEach((sample, index) => {
        const inside = sample.score >= threshold

        if (inside && runStart < 0) runStart = index

        if ((!inside || index === samples.length - 1) && runStart >= 0) {
            const runEnd = inside ? index : index - 1
            const duration = samples[runEnd].time.getTime() - samples[runStart].time.getTime()
            const average = samples.slice(runStart, runEnd + 1).reduce((sum, item) => sum + item.score, 0) / (runEnd - runStart + 1)

            if (!best || duration > best.end.getTime() - best.start.getTime()) {
                best = { start: samples[runStart].time, end: samples[runEnd].time, score: average }
            }

            runStart = -1
        }
    })

    return best
}

const HOUR_WORDS = ['twelve', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven']

/** "after nine", "after half past ten": how people actually say evening times. */
export const spokenTime = (date: Date): string => {
    const { hour, minute } = zonedParts(date)
    const rounded = Math.round(minute / 15) * 15
    const baseHour = rounded === 60 ? hour + 1 : hour
    const quarter = rounded % 60
    const word = (h: number) => HOUR_WORDS[((h % 12) + 12) % 12]

    if (quarter === 0) return baseHour % 24 === 0 ? 'midnight' : word(baseHour)
    if (quarter === 15) return `quarter past ${word(baseHour)}`
    if (quarter === 30) return `half past ${word(baseHour)}`

    return `quarter to ${word(baseHour + 1)}`
}

export type NamedStar = {
    name: string
    vector: [number, number, number]
}

const angleBetween = (a: [number, number, number], b: [number, number, number]) => {
    const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2]

    return Math.acos(Math.max(-1, Math.min(1, dot))) * (180 / Math.PI)
}

/** One plain sentence about the best thing to look for tonight. */
export const tonightLine = (events: NightEvents, planets: PlanetNight[], namedStars: NamedStar[] = []): string => {
    const candidates = planets
        .filter(planet => planet.nakedEye && planet.visibleFrom && planet.bestTime)
        .sort((a, b) => a.magnitude - b.magnitude)

    const evening = candidates.find(planet => {
        const { hour } = zonedParts(planet.visibleFrom as Date)

        return hour >= 15 && hour <= 23
    })

    const featured = evening ?? candidates[0]
    const moon = getMoonState(events.darkStart)

    if (!featured) {
        if (moon.fraction < 0.35) return 'No bright planets tonight, and the Moon keeps out of the way. A good night to find the Milky Way overhead once your eyes adjust.'

        return `No bright planets tonight. The ${moon.name.toLowerCase()} lights the sky, so look for the brightest stars and the shapes of the constellations.`
    }

    const { hour } = zonedParts(featured.visibleFrom as Date)
    const morning = hour >= 3 && hour < 12
    const featuredBody = PLANETS.find(planet => planet.key === featured.key)?.body
    // Before dawn, describe it at its best; in the evening, where it is when it appears.
    const place = !morning && featuredBody ? altitudeOf(featuredBody, featured.visibleFrom as Date) : { alt: featured.bestAlt, az: featured.bestAz }
    const height = place.alt < 22 ? 'low' : place.alt < 45 ? 'well placed' : 'high'
    const direction = COMPASS_WORDS[compassPoint(place.az)]
    const dusk = events.civilDusk ? Math.abs((featured.visibleFrom as Date).getTime() - events.civilDusk.getTime()) < 40 * 60 * 1000 : false
    const timing = morning ? 'before dawn' : dusk ? 'from dusk' : `after ${spokenTime(featured.visibleFrom as Date)}`
    const near = namedStars
        .map(star => ({ star, distance: angleBetween(star.vector, featured.vector) }))
        .filter(item => item.distance < 14)
        .sort((a, b) => a.distance - b.distance)[0]

    const look = near
        ? `Look for a ${featured.look}, not far from ${near.star.name}.`
        : `Look for a ${featured.look}.`

    return `${featured.name} is ${height} in the ${direction} ${timing}, in ${featured.constellation}. ${look}`
}
